import React, { useCallback, useEffect, useState } from "react";
import {
  getStandardCourse, getStandardCourseChecklist, getStandardCourseDraft, listStandardCourseAhaProducts, startStandardCourseVersion,
} from "../../../api/backend";
import { Cat, Crumbs, Notice, PageHeader, StatePanel, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";
import DetailsPanel from "./DetailsPanel";
import SessionsPanel from "./SessionsPanel";
import SkillSheetsPanel from "./SkillSheetsPanel";
import TestsPanel from "./TestsPanel";
import DocumentsPanel from "./DocumentsPanel";
import PublishPanel from "./PublishPanel";
import IntegrationsTab from "./integrations/IntegrationsTab";
import ReadyToGoLive from "./integrations/ReadyToGoLive";
import { errorText } from "./common";

/**
 * Builds the draft of one standard course. Nothing here touches a training center, and nothing is live until the draft
 * is published. Each tab shows whether it is complete, so problems are visible before the review tab.
 */
export default function StandardCourseEditor({ courseId, onBack, onPublished, initialTab = "details" }) {
  const [draft, setDraft] = useState(null);
  const [course, setCourse] = useState(null);
  const [ahaProducts, setAhaProducts] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [error, setError] = useState(null);
  const [noDraft, setNoDraft] = useState(false);
  const [tab, setTab] = useState(initialTab);

  const loadCourse = useCallback(() => getStandardCourse(courseId).then(setCourse).catch(() => setCourse(null)), [courseId]);
  // The tab badge and Ready to go live need the linked products; a failure leaves them out rather than blocking the editor.
  const loadAha = useCallback(() => listStandardCourseAhaProducts(courseId).then(setAhaProducts).catch(() => setAhaProducts(null)), [courseId]);
  const integrationsChanged = useCallback(() => { loadAha(); loadCourse(); }, [loadAha, loadCourse]);

  const loadChecklist = useCallback(() => getStandardCourseChecklist(courseId)
    .then(setChecklist)
    .catch((e) => setError(errorText(e, "The checklist could not be loaded."))), [courseId]);

  const load = useCallback(() => getStandardCourseDraft(courseId)
    .then((d) => { setDraft(d); setNoDraft(false); setError(null); loadChecklist(); })
    .catch((e) => {
      if (e?.response?.status === 409) setNoDraft(true);
      else setError(errorText(e, "The course could not be loaded."));
    }), [courseId, loadChecklist]);

  useEffect(() => { load(); loadCourse(); loadAha(); }, [load, loadCourse, loadAha]);

  const startVersion = async () => {
    try {
      await startStandardCourseVersion(courseId);
      await load();
    } catch (e) {
      setError(errorText(e, "A new version could not be started."));
    }
  };

  if (noDraft) {
    const retired = course?.status === "RETIRED";
    const title = course?.title || "Standard course";
    return (
      <div data-testid="standard-editor-no-draft">
        <PageHeader
          title={title}
          crumbs={<Crumbs trail={[{ label: "Standardize courses", onClick: onBack }, { label: title }]} />}
          desc={(
            <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {course?.courseNumber && <span className="code">{course.courseNumber}</span>}
              {course?.category && <Cat c={course.category} />}
              {retired ? <StatusChip status="Retired" label="Retired · read-only" />
                : course?.currentVersionNumber ? <StatusChip status="Published" label={`Live · v${course.currentVersionNumber} · frozen`} /> : null}
            </span>
          )}
        />
        <Notice kind="info">
          {retired
            ? "This course is retired. Its published versions cannot be changed."
            : <>This course has no draft. A published version cannot be changed: start a new version to make changes.{" "}
              <button type="button" className="b sec sm" onClick={startVersion}>Start a new version</button></>}
        </Notice>
        {error && <div style={{ marginTop: 16 }}><Notice kind="bad">{error}</Notice></div>}
        <div style={{ marginTop: 16 }}>
          <IntegrationsTab courseId={courseId} course={course} onChanged={integrationsChanged} />
        </div>
      </div>
    );
  }
  if (!draft) {
    return error
      ? <div><button type="button" className="b ghost" style={{ marginBottom: 12 }} onClick={onBack}><I.back s={14} />Back</button><Notice kind="bad">{error}</Notice></div>
      : <StatePanel kind="loading" what="the draft" />;
  }

  const shared = { draft, courseId, onChanged: load };
  const badges = tabBadges(draft, checklist, ahaProducts);
  const live = Boolean(draft.course.currentVersionId);

  return (
    <div data-testid="standard-course-editor">
      <div className="notice info" style={{ marginBottom: 18, border: "1px solid rgba(29,78,216,.25)" }}>
        <I.pencil s={15} />
        <div>
          <b>Draft v{draft.version.versionNumber} · not visible to training centers.</b>{" "}
          This course belongs to no training center. Changes are saved to the draft and are not live until you publish.
          {live && <span style={{ float: "right", marginLeft: 12 }}>Centers currently pull <b>v{draft.course.currentVersionNumber ?? "—"}</b></span>}
        </div>
      </div>
      <PageHeader
        title={draft.version.title || draft.course.title}
        crumbs={<Crumbs trail={[{ label: "Standardize courses", onClick: onBack }, { label: draft.version.title || draft.course.title }]} />}
        desc={(
          <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {draft.course.courseNumber && <span className="code">{draft.course.courseNumber}</span>}
            {draft.course.category && <Cat c={draft.course.category} />}
            <StatusChip status="Draft" label={`Version ${draft.version.versionNumber} · draft`} />
            {live && <StatusChip status="Published" label={`Centers pull v${draft.course.currentVersionNumber ?? "—"}`} />}
          </span>
        )}
        actions={<button type="button" className="b pri" onClick={() => setTab("publish")}>Review and publish</button>}
      />
      {error && <div style={{ marginBottom: 16 }}><Notice kind="bad">{error} <button type="button" className="b ghost sm" onClick={() => setError(null)}>Dismiss</button></Notice></div>}

      <div className="vtabs" role="tablist" aria-label="Course sections">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "on" : ""} onClick={() => setTab(key)}>
            {label}<TabBadge badge={badges[key]} />
          </button>
        ))}
      </div>

      {tab === "details" && <DetailsPanel draft={draft} courseId={courseId} onSaved={load} />}
      {tab === "sessions" && <SessionsPanel {...shared} />}
      {tab === "skills" && <SkillSheetsPanel {...shared} />}
      {tab === "tests" && <TestsPanel {...shared} />}
      {tab === "documents" && <DocumentsPanel {...shared} />}
      {tab === "integrations" && (
        <IntegrationsTab courseId={courseId} course={course || { ...draft.course, status: "ACTIVE" }} onChanged={integrationsChanged} />
      )}
      {tab === "publish" && (
        <>
          <ReadyToGoLive products={ahaProducts} versionNumber={draft.version.versionNumber} setTab={setTab} />
          <PublishPanel draft={draft} courseId={courseId} checklist={checklist} onRecheck={loadChecklist} onPublished={onPublished || onBack} />
        </>
      )}
    </div>
  );
}

const TABS = [
  ["details", "Details"],
  ["sessions", "Sessions"],
  ["skills", "Skill sheets"],
  ["tests", "Tests"],
  ["documents", "Documents"],
  ["integrations", "Integrations"],
  ["publish", "Review and publish"],
];

/** Badge for each tab: Missing, a count, or a check. The review tab counts the checklist's blocking problems. */
export function tabBadges(draft, checklist, ahaProducts = null) {
  const count = (n) => ({ kind: "count", text: String(n) });
  const missing = { kind: "missing", text: "Missing" };
  const done = { kind: "ok", text: "✓" };
  const detailsMissing = draft.version.totalHours == null || !String(draft.version.description || "").trim();
  const problems = checklist?.problems?.length ?? null;
  return {
    details: detailsMissing ? missing : done,
    sessions: draft.sessions.length ? count(draft.sessions.length) : missing,
    skills: count(draft.skillSheets.length),
    tests: draft.tests.length ? count(draft.tests.length) : missing,
    documents: count(draft.documents.length),
    integrations: ahaProducts == null ? null : ahaProducts.length ? count(ahaProducts.length) : { kind: "none", text: "None" },
    publish: problems == null ? null : problems ? { kind: "missing", text: `${problems} ${problems === 1 ? "problem" : "problems"}` } : done,
  };
}

/** The small badge on a tab: red when something is missing, green when complete, grey for a count. */
function TabBadge({ badge }) {
  if (!badge) return null;
  const cls = badge.kind === "missing" ? "miss" : badge.kind === "ok" ? "ok" : "";
  return <span className={`tbadge ${cls}`.trim()}>{badge.text}</span>;
}
